/**
 * Render helpers for PerformanceAnalyticsView
 *
 * Extracted from PerformanceAnalyticsView to reduce file length and cognitive
 * complexity. Contains the header (mobile + desktop) and submenu (mobile + desktop)
 * rendering logic for the compare/timeframe dropdowns.
 * SE-5551: the compare/timeframe dropdowns render through the
 * `FixedPositionDropdownShell` atom, which owns trigger-anchored measurement +
 * portal + z-order. On native it returns static `position: absolute` geometry
 * available on the first open frame, fixing the Android mispositioning the
 * gluestack Menu suffered (unbounded absolute chain capped only by a hardcoded
 * maxHeight). Positioning/open-state come from the Container via `useDropdownPositioning`.
 * @remarks Pure rendering helpers — no hooks, state, or side effects
 */

import { Check, Info, Search, X } from "lucide-react-native";
import React from "react";

import { ScrollView } from "@/components/atoms/ScrollView";

import { CompareSubmenuRow } from "@/features/player-detail/components/molecules/CompareSubmenuRow";
import FullScreenOverlay from "@/components/organisms/shared/FullScreenOverlay";
import type { PerformanceShellTriggerProps } from "@/features/player-detail/hooks/usePerformanceMenuTriggers";

import type {
  CompareItem,
  TimeframeOption,
} from "@/features/player-detail/types";

import { Box } from "@/components/atoms/Box";
import { Button } from "@/components/atoms/Button";
import { Heading } from "@/components/atoms/Heading";
import { Stack } from "@/components/atoms/Stack";
import { Icon } from "@/components/atoms/Icon";
import { Input } from "@/components/atoms/Input";
import {
  FixedPositionDropdownShell,
  FixedPositionDropdownShellBackdrop,
  FixedPositionDropdownShellPanel,
  type DropdownMeasurableNode,
  type DropdownPanelGeometry,
} from "@/components/atoms/FixedPositionDropdownShell";
import { Pressable } from "@/components/atoms/Pressable";
import { Text } from "@/components/atoms/Text";

/**
 * Positioning + open-state wiring for one shell-hosted dropdown, supplied by
 * the Container's `useDropdownPositioning` (SE-5551).
 */
export interface ShellDropdownWiring {
  /** Whether the panel is open. */
  readonly isOpen: boolean;
  /** Toggle the panel open/closed (trigger + backdrop). */
  readonly onToggle: () => void;
  /** Measurement ref from `useDropdownPositioning`. */
  readonly triggerRef: React.RefObject<DropdownMeasurableNode | null>;
  /** Style-ready panel geometry (null until measured). */
  readonly panelGeometry: DropdownPanelGeometry | null;
  /** Flicker guard — overlay renders only when true. */
  readonly shouldShow: boolean;
}

/**
 * Shared params for header rendering helpers
 */
interface HeaderParams {
  readonly theme: { readonly headingClass: string; readonly textClass: string };
  readonly isDark: boolean;
  readonly onOpenInfoModal: () => void;
  readonly renderCompareMenuTrigger: (
    props: PerformanceShellTriggerProps
  ) => React.JSX.Element;
  readonly compareCategoryOptions: readonly CompareItem[];
  readonly createCompareCategorySelectHandler: (
    item: CompareItem
  ) => () => void;
  readonly selectedCompareCategoryType: string;
  readonly compareDropdown: ShellDropdownWiring;
  readonly renderTimeframeMenuTrigger: (
    props: PerformanceShellTriggerProps
  ) => React.JSX.Element;
  readonly timeframeOptions: readonly TimeframeOption[];
  readonly createTimeframeSelectHandler: (
    timeframe: TimeframeOption
  ) => () => void;
  readonly selectedTimeframe: TimeframeOption | null;
  readonly timeframeDropdown: ShellDropdownWiring;
}

/**
 * One option row inside a shell dropdown panel — preserves the pre-migration
 * look (selected = accent-primary label + right-aligned check; unselected = theme text)
 * and carries `accessibilityRole="menuitem"` for the SE-5149 e2e contract.
 *
 * SE-6828: the label is truncated with the same contract the compare/timeframe
 * TRIGGER labels use (`usePerformanceMenuTriggers`) — `min-w-0` + `shrink`,
 * `numberOfLines={1}` and `isTruncated`. `min-w-0` is load-bearing: in React
 * Native a flex child will not shrink below its intrinsic content width without
 * it (RN's `flexShrink` also defaults to 0, unlike web's 1), so `shrink` alone
 * was inert and the label overflowed the row. With nothing requesting
 * single-line truncation, the overflow was then hard-clipped mid-word by the
 * panel's ancestor overflow rather than ellipsised.
 * @param params - Row params.
 * @param params.id - Stable option id (React key + selection compare).
 * @param params.label - Visible option label.
 * @param params.isSelected - Whether this option is the current selection.
 * @param params.onPress - Selection handler.
 * @param params.isDark - Whether dark mode is active.
 * @returns The option Pressable.
 */
export const renderShellOption = ({
  id,
  label,
  isSelected,
  onPress,
  isDark,
}: {
  readonly id: string;
  readonly label: string;
  readonly isSelected: boolean;
  readonly onPress: () => void;
  readonly isDark: boolean;
}): React.JSX.Element => (
  <Pressable
    key={id}
    accessibilityRole="menuitem"
    accessibilityLabel={label}
    accessibilityState={{ selected: isSelected }}
    onPress={onPress}
    className="w-full flex-row items-center justify-between gap-2 px-3 py-2"
  >
    <Text
      variant={isSelected ? "body-strong" : "body"}
      className={`min-w-0 shrink ${
        isSelected
          ? "text-accent-primary"
          : isDark
            ? "text-white"
            : "text-content-secondary"
      }`}
      isTruncated
      numberOfLines={1}
    >
      {label}
    </Text>
    {isSelected ? (
      <Icon as={Check} size="sm" className="text-accent-primary" />
    ) : null}
  </Pressable>
);

/**
 * Compose a trigger + shell-anchored option panel for one dropdown. Shared by
 * the mobile + desktop compare/timeframe selectors (SE-5551).
 * @param params - Dropdown params.
 * @param params.wiring - Open-state + positioning from the Container.
 * @param params.trigger - The shell-hosted trigger element.
 * @param params.options - Option rows to render.
 * @returns The FixedPositionDropdownShell tree.
 */
const renderShellDropdown = ({
  wiring,
  trigger,
  options,
}: {
  readonly wiring: ShellDropdownWiring;
  readonly trigger: React.JSX.Element;
  readonly options: readonly React.JSX.Element[];
}): React.JSX.Element => (
  <FixedPositionDropdownShell>
    {trigger}
    {wiring.shouldShow ? (
      <>
        <FixedPositionDropdownShellBackdrop
          onPress={wiring.onToggle}
          accessibilityLabel="Close dropdown"
        />
        <FixedPositionDropdownShellPanel
          geometry={wiring.panelGeometry}
          onDismiss={wiring.onToggle}
          className="max-w-72 overflow-hidden py-1"
        >
          <ScrollView>{options}</ScrollView>
        </FixedPositionDropdownShellPanel>
      </>
    ) : null}
  </FixedPositionDropdownShell>
);

/**
 * The "Performance Analytics" heading + info button, shared by both header
 * layouts (SE-5551 — deduped so mobile and desktop don't repeat the block).
 * @param params - Heading params.
 * @param params.theme - Theme classes for headings and text.
 * @param params.onOpenInfoModal - Callback to open the info modal.
 * @returns The heading row.
 */
const renderHeadingRow = ({
  theme,
  onOpenInfoModal,
}: Pick<HeaderParams, "theme" | "onOpenInfoModal">): React.JSX.Element => (
  <Stack direction="horizontal" className="items-center gap-2">
    <Heading variant="title-lg" className={theme.headingClass}>
      Performance Analytics
    </Heading>
    <Pressable onPress={onOpenInfoModal} hitSlop={8}>
      <Icon as={Info} size="sm" className="text-content-muted" />
    </Pressable>
  </Stack>
);

/**
 * The compare-vs. dropdown field (trigger + shell-anchored option panel),
 * shared by both header layouts (SE-5551).
 * @param params - Header params (uses the compare-related fields).
 * @returns The compare dropdown field.
 */
const renderCompareField = (params: HeaderParams): React.JSX.Element =>
  renderShellDropdown({
    wiring: params.compareDropdown,
    trigger: params.renderCompareMenuTrigger({
      isOpen: params.compareDropdown.isOpen,
      onPress: params.compareDropdown.onToggle,
      triggerRef: params.compareDropdown.triggerRef,
    }),
    options: params.compareCategoryOptions.map(item =>
      renderShellOption({
        id: item.id,
        label: item.name,
        isSelected: item.id === params.selectedCompareCategoryType,
        onPress: params.createCompareCategorySelectHandler(item),
        isDark: params.isDark,
      })
    ),
  });

/**
 * The timeframe dropdown field (trigger + shell-anchored option panel),
 * shared by both header layouts (SE-5551).
 * @param params - Header params (uses the timeframe-related fields).
 * @returns The timeframe dropdown field.
 */
const renderTimeframeField = (params: HeaderParams): React.JSX.Element =>
  renderShellDropdown({
    wiring: params.timeframeDropdown,
    trigger: params.renderTimeframeMenuTrigger({
      isOpen: params.timeframeDropdown.isOpen,
      onPress: params.timeframeDropdown.onToggle,
      triggerRef: params.timeframeDropdown.triggerRef,
    }),
    options: params.timeframeOptions.map(option =>
      renderShellOption({
        id: option.id,
        label: option.label,
        isSelected: option.id === params.selectedTimeframe?.id,
        onPress: params.createTimeframeSelectHandler(option),
        isDark: params.isDark,
      })
    ),
  });

/**
 * Render the mobile header with side-by-side compare/timeframe dropdowns
 * Labels sit above their respective dropdown, both columns share equal width
 * @param params - Header rendering parameters
 * @param params.theme - Theme classes for headings and text
 * @param params.isDark - Whether dark mode is active
 * @param params.onOpenInfoModal - Callback to open the info modal
 * @param params.renderCompareMenuTrigger - Render function for compare menu trigger
 * @param params.compareCategoryOptions - Compare category options
 * @param params.createCompareCategorySelectHandler - Factory for compare category handlers
 * @param params.selectedCompareCategoryType - Currently selected compare category type
 * @param params.compareDropdown - Shell open-state + positioning for the compare dropdown
 * @param params.renderTimeframeMenuTrigger - Render function for timeframe menu trigger
 * @param params.timeframeOptions - Available timeframe options
 * @param params.createTimeframeSelectHandler - Factory for timeframe handlers
 * @param params.selectedTimeframe - Currently selected timeframe
 * @param params.timeframeDropdown - Shell open-state + positioning for the timeframe dropdown
 * @returns VStack with the mobile header layout
 */
export const renderMobileHeader = (params: HeaderParams): React.JSX.Element => (
  <Stack className="relative z-20 gap-3 overflow-visible">
    {renderHeadingRow(params)}
    <Stack
      direction="horizontal"
      className="items-start gap-3 overflow-visible"
    >
      <Stack className="flex-1 gap-1 overflow-visible">
        <Text
          variant="unstyled"
          className={`text-body ${params.theme.textClass}`}
        >
          Compare vs.
        </Text>
        {renderCompareField(params)}
      </Stack>
      <Stack className="flex-1 gap-1 overflow-visible">
        <Text
          variant="unstyled"
          className={`text-body ${params.theme.textClass}`}
        >
          Timeframe:
        </Text>
        {renderTimeframeField(params)}
      </Stack>
    </Stack>
  </Stack>
);

/**
 * Render the desktop header with side-by-side compare/timeframe dropdowns
 * @param params - Header rendering parameters
 * @param params.theme - Theme classes for headings and text
 * @param params.isDark - Whether dark mode is active
 * @param params.onOpenInfoModal - Callback to open the info modal
 * @param params.renderCompareMenuTrigger - Render function for compare menu trigger
 * @param params.compareCategoryOptions - Compare category options
 * @param params.createCompareCategorySelectHandler - Factory for compare category handlers
 * @param params.selectedCompareCategoryType - Currently selected compare category type
 * @param params.compareDropdown - Shell open-state + positioning for the compare dropdown
 * @param params.renderTimeframeMenuTrigger - Render function for timeframe menu trigger
 * @param params.timeframeOptions - Available timeframe options
 * @param params.createTimeframeSelectHandler - Factory for timeframe handlers
 * @param params.selectedTimeframe - Currently selected timeframe
 * @param params.timeframeDropdown - Shell open-state + positioning for the timeframe dropdown
 * @returns HStack with the desktop header layout
 */
export const renderDesktopHeader = (
  params: HeaderParams
): React.JSX.Element => {
  return (
    <Stack
      direction="horizontal"
      className="relative z-20 items-center justify-between overflow-visible"
    >
      {renderHeadingRow(params)}
      <Stack
        direction="horizontal"
        className="items-center gap-3 overflow-visible"
      >
        <Stack direction="horizontal" className="items-center gap-2">
          <Text
            variant="unstyled"
            className={`text-body ${params.theme.textClass}`}
          >
            Compare vs.
          </Text>
          {renderCompareField(params)}
        </Stack>
        <Stack direction="horizontal" className="items-center gap-2">
          <Text
            variant="unstyled"
            className={`text-body ${params.theme.textClass}`}
          >
            Timeframe:
          </Text>
          {renderTimeframeField(params)}
        </Stack>
      </Stack>
    </Stack>
  );
};

/**
 * Shared params for submenu rendering helpers
 */
interface SubmenuParams {
  readonly isDark: boolean;
  readonly isSubmenuOpen: boolean;
  readonly submenuTitle: string;
  readonly submenuSearchPlaceholder: string;
  readonly submenuSearchQuery: string;
  readonly filteredSubmenuItems: readonly CompareItem[];
  readonly isSubmenuLoading: boolean;
  /** Whether a further page of results can be fetched (SE-7111) */
  readonly hasMoreSubmenuItems: boolean;
  /** Whether the next page is currently in flight (SE-7111) */
  readonly isLoadingMoreSubmenuItems: boolean;
  /** Fetches the next page of results (SE-7111) */
  readonly onLoadMoreSubmenuItems: () => void;
  readonly onCloseSubmenu: () => void;
  readonly onSelectSubmenuItem: (item: CompareItem) => void;
  readonly onSubmenuSearchChange: (query: string) => void;
}

/**
 * The submenu's next-page control (SE-7111).
 *
 * Rendered as an explicit press target rather than scroll-triggered paging
 * because the two submenu surfaces scroll through different owners — the
 * desktop panel's own `ScrollView`, the mobile overlay's `ModalBody` — so one
 * button behaves identically on both without nesting a virtualized list inside
 * a scroll view. Shown only when the connection reports another page, so a
 * fully-listed result set carries no dead affordance.
 * @param isLoadingMore - Whether the next page is already in flight
 * @param onLoadMore - Fetches the next page
 * @returns The load-more Button
 */
const renderSubmenuLoadMoreButton = (
  isLoadingMore: boolean,
  onLoadMore: () => void
): React.JSX.Element => (
  <Button
    label="Load more"
    variant="outline"
    tone="neutral"
    size="sm"
    isLoading={isLoadingMore}
    onPress={onLoadMore}
    testID="compare-submenu-load-more"
  />
);

/**
 * Get modal backdrop classes based on theme
 * @param isDark - Whether dark mode is active
 * @returns Tailwind classes for modal backdrop
 */
const getBackdropClasses = (isDark: boolean): string =>
  `fixed inset-0 ${isDark ? "bg-black/50" : "bg-black/30"}`;

/**
 * Returns the VStack class for the desktop submenu content panel
 */
const getSubmenuContentClass = (): string =>
  "z-50 w-96 overflow-hidden rounded-md bg-surface-base shadow-hard-2";

/**
 * Returns the Input container class for the submenu search input
 */
const getSubmenuInputContainerClass = (): string =>
  "rounded-md border-outline-subtle bg-surface-raised";

/**
 * Render the mobile full-screen submenu overlay
 * @param params - Submenu rendering parameters
 * @param params.isDark - Whether dark mode is active
 * @param params.isSubmenuOpen - Whether the submenu is open
 * @param params.submenuTitle - Title for the submenu modal
 * @param params.submenuSearchPlaceholder - Placeholder text for search
 * @param params.submenuSearchQuery - Current search query
 * @param params.filteredSubmenuItems - Filtered items to display
 * @param params.isSubmenuLoading - Whether search results are loading
 * @param params.hasMoreSubmenuItems - Whether a further page can be fetched
 * @param params.isLoadingMoreSubmenuItems - Whether the next page is in flight
 * @param params.onLoadMoreSubmenuItems - Fetches the next page of results
 * @param params.onCloseSubmenu - Callback to close the submenu
 * @param params.onSelectSubmenuItem - Callback when an item is selected
 * @param params.onSubmenuSearchChange - Callback for search input change
 * @returns FullScreenOverlay with search and item list
 */
export const renderMobileSubmenu = ({
  isDark,
  isSubmenuOpen,
  submenuTitle,
  submenuSearchPlaceholder,
  submenuSearchQuery,
  filteredSubmenuItems,
  isSubmenuLoading,
  hasMoreSubmenuItems,
  isLoadingMoreSubmenuItems,
  onLoadMoreSubmenuItems,
  onCloseSubmenu,
  onSelectSubmenuItem,
  onSubmenuSearchChange,
}: SubmenuParams): React.JSX.Element => (
  <FullScreenOverlay
    isOpen={isSubmenuOpen}
    onClose={onCloseSubmenu}
    title={submenuTitle}
  >
    <Stack className="gap-0">
      <Box className="py-3">
        <Input
          size="md"
          variant="outline"
          className="rounded-sm border-outline-subtle bg-surface-raised"
          leadingIcon={Search}
          field={{
            value: submenuSearchQuery,
            onChangeText: onSubmenuSearchChange,
            placeholder: submenuSearchPlaceholder,
            placeholderTextColor: isDark ? "#9CA3AF" : "#6B7280",
            className: "text-content-primary",
          }}
        />
      </Box>
      {filteredSubmenuItems.map(item => (
        <CompareSubmenuRow
          key={item.id}
          item={item}
          isDark={isDark}
          onSelect={onSelectSubmenuItem}
        />
      ))}
      {filteredSubmenuItems.length === 0 && (
        <Box className="py-8">
          <Text variant="body" className="text-center text-content-subtle">
            {isSubmenuLoading ? "Searching..." : "No results found"}
          </Text>
        </Box>
      )}
      {hasMoreSubmenuItems && (
        <Box className="py-3">
          {renderSubmenuLoadMoreButton(
            isLoadingMoreSubmenuItems,
            onLoadMoreSubmenuItems
          )}
        </Box>
      )}
    </Stack>
  </FullScreenOverlay>
);

/**
 * Returns the empty-state label for the submenu item list
 * @param isLoading - Whether results are currently loading
 */
const getSubmenuEmptyText = (isLoading: boolean): string =>
  isLoading ? "Searching..." : "No results found";

/**
 * Render the desktop centered submenu modal
 * @param params - Submenu rendering parameters
 * @param params.isDark - Whether dark mode is active
 * @param params.isSubmenuOpen - Whether the submenu is open
 * @param params.submenuTitle - Title for the submenu modal
 * @param params.submenuSearchPlaceholder - Placeholder text for search
 * @param params.submenuSearchQuery - Current search query
 * @param params.filteredSubmenuItems - Filtered items to display
 * @param params.isSubmenuLoading - Whether search results are loading
 * @param params.hasMoreSubmenuItems - Whether a further page can be fetched
 * @param params.isLoadingMoreSubmenuItems - Whether the next page is in flight
 * @param params.onLoadMoreSubmenuItems - Fetches the next page of results
 * @param params.onCloseSubmenu - Callback to close the submenu
 * @param params.onSelectSubmenuItem - Callback when an item is selected
 * @param params.onSubmenuSearchChange - Callback for search input change
 * @returns Positioned modal with search and scrollable item list, or null when closed
 */
export const renderDesktopSubmenu = ({
  isDark,
  isSubmenuOpen,
  submenuTitle,
  submenuSearchPlaceholder,
  submenuSearchQuery,
  filteredSubmenuItems,
  isSubmenuLoading,
  hasMoreSubmenuItems,
  isLoadingMoreSubmenuItems,
  onLoadMoreSubmenuItems,
  onCloseSubmenu,
  onSelectSubmenuItem,
  onSubmenuSearchChange,
}: SubmenuParams): React.JSX.Element | null =>
  isSubmenuOpen ? (
    <Box className="fixed inset-0 z-50 items-center justify-center">
      <Pressable
        className={getBackdropClasses(isDark)}
        onPress={onCloseSubmenu}
      />
      <Stack className={getSubmenuContentClass()}>
        <Stack
          direction="horizontal"
          className="items-center justify-between px-5 pb-2 pt-5"
        >
          <Text variant="title-lg" className="text-content-primary">
            {submenuTitle}
          </Text>
          <Pressable onPress={onCloseSubmenu} className="p-1">
            <Icon as={X} size="md" className="text-content-muted" />
          </Pressable>
        </Stack>
        <Box className="px-5 py-3">
          <Input
            size="md"
            variant="outline"
            className={getSubmenuInputContainerClass()}
            leadingIcon={Search}
            field={{
              value: submenuSearchQuery,
              onChangeText: onSubmenuSearchChange,
              placeholder: submenuSearchPlaceholder,
              placeholderTextColor: isDark ? "#9CA3AF" : "#6B7280",
              className: "text-content-primary",
            }}
          />
        </Box>
        <ScrollView className="max-h-80" showsVerticalScrollIndicator>
          {filteredSubmenuItems.map(item => (
            <CompareSubmenuRow
              key={item.id}
              item={item}
              isDark={isDark}
              onSelect={onSelectSubmenuItem}
            />
          ))}
          {filteredSubmenuItems.length === 0 && (
            <Box className="px-5 py-8">
              <Text variant="body" className="text-center text-content-subtle">
                {getSubmenuEmptyText(isSubmenuLoading)}
              </Text>
            </Box>
          )}
          {hasMoreSubmenuItems && (
            <Box className="px-5 py-3">
              {renderSubmenuLoadMoreButton(
                isLoadingMoreSubmenuItems,
                onLoadMoreSubmenuItems
              )}
            </Box>
          )}
        </ScrollView>
      </Stack>
    </Box>
  ) : null;
